import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { apiLogin, apiImpersonate, fetchUserPreferences } from '../services/api';
import {
  IMP_KEY, LEGACY_TOKEN_KEY, SessionEndedError, cacheUser, clearLocalSession, getAccessToken,
  onSessionEnded, readCachedUser, readImpStash, refreshSession, serverLogout, setAccessToken, setSessionUserId,
  startSessionKeepAlive, upgradeLegacyToken,
} from '../services/session';

const AuthContext = createContext(null);

const endedMessage = (detail) => (
  /disabled/i.test(detail || '')
    ? 'Your account has been disabled. Contact your administrator.'
    : 'Your session has expired. Please sign in again.'
);

// Restore a session on app open: cookie refresh first, then one-time legacy upgrade.
const restoreSession = async () => {
  try {
    return await refreshSession();
  } catch (e) {
    const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
    if (!(e instanceof SessionEndedError) || !legacy) throw e;
    try {
      return await upgradeLegacyToken(legacy);
    } finally {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offlineSession, setOfflineSession] = useState(false);
  const [impersonating, setImpersonating] = useState(() => !!readImpStash());

  const userRef = useRef(null);
  userRef.current = user;

  const handleEnded = useCallback((detail) => {
    if (userRef.current) toast.error(endedMessage(detail), { id: 'session-ended' });
    setUser(null);
    setImpersonating(false);
    setOfflineSession(false);
  }, []);

  useEffect(() => onSessionEnded(handleEnded), [handleEnded]);

  useEffect(() => {
    startSessionKeepAlive();
    restoreSession()
      .then((u) => {
        setUser(u);
        setImpersonating(!!readImpStash());
        fetchUserPreferences().catch(() => { /* ignore */ });
      })
      .catch((e) => {
        if (e instanceof SessionEndedError) {
          if (/disabled/i.test(e.detail)) toast.error(endedMessage(e.detail), { id: 'session-ended' });
          clearLocalSession();
          return;
        }
        // Offline / server unreachable: fall back to the cached profile, read-only.
        const cached = readCachedUser();
        if (cached) { setUser(cached); setOfflineSession(true); }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!offlineSession) return undefined;
    const retry = () => refreshSession()
      .then((u) => { setUser(u); setOfflineSession(false); })
      .catch((e) => { if (e instanceof SessionEndedError) { clearLocalSession(); handleEnded(e.detail); } });
    window.addEventListener('online', retry);
    const t = setInterval(() => { if (navigator.onLine) retry(); }, 30_000);
    return () => { window.removeEventListener('online', retry); clearInterval(t); };
  }, [offlineSession, handleEnded]);

  const setSession = (token, u) => {
    localStorage.removeItem(IMP_KEY);
    setImpersonating(false);
    setOfflineSession(false);
    setAccessToken(token);
    setSessionUserId(u.id);
    cacheUser(u);
    setUser(u);
  };

  const login = async (email, password) => {
    const { token, user: u } = await apiLogin(email, password);
    setSession(token, u);
    fetchUserPreferences().catch(() => { /* ignore */ });
    return u;
  };

  const logout = async () => {
    setUser(null);
    setImpersonating(false);
    setOfflineSession(false);
    const done = serverLogout(); // reads the device credential before it is cleared
    clearLocalSession();
    await done;
  };

  // Admin-only: swap into a customer account. The admin session (cookie)
  // stays put; only the target id is remembered so refresh can re-mint.
  const startImpersonation = async (customerId) => {
    if (!getAccessToken() || !user) throw new Error('Not signed in');
    const { token, user: target } = await apiImpersonate(customerId);
    localStorage.setItem(IMP_KEY, JSON.stringify({ admin: user, targetId: customerId }));
    setAccessToken(token);
    cacheUser(target);
    setUser(target);
    setImpersonating(true);
    return target;
  };

  const stopImpersonation = async () => {
    if (!readImpStash()) return null;
    localStorage.removeItem(IMP_KEY);
    setImpersonating(false);
    try {
      const admin = await refreshSession();
      setUser(admin);
      return admin;
    } catch (e) {
      if (e instanceof SessionEndedError) handleEnded(e.detail);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user, login, logout, loading, setSession, offlineSession,
      impersonating, startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
};
